import type { ReactNode } from 'react';
import {
  LayoutDashboard,
  Activity,
  FileText,
  Target,
  Folder,
  Settings,
  Users,
  ClipboardList,
  ClipboardCheck,
  BarChart3,
  ShieldCheck,
  Search,
  Bell,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Logo } from './Logo';

type MenuItem = {
  key: string;
  label: string;
  icon: LucideIcon;
  ready?: boolean;
  route?: string;
  /** When true, the item is only shown to a global admin (hidden for a coach). */
  adminOnly?: boolean;
};

const OWNER_MENU: MenuItem[] = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, ready: true, route: '/' },
  { key: 'gcu', label: 'GCU Check-Up', icon: Activity, ready: true, route: '/intake/upload' },
  { key: 'verify', label: 'Verifikasi Owner', icon: ClipboardCheck, ready: true, route: '/verify' },
  { key: 'reports', label: 'Reports', icon: FileText, ready: true, route: '/report' },
  { key: 'coaching', label: 'Coaching', icon: Target, ready: true, route: '/coaching' },
  { key: 'documents', label: 'Documents', icon: Folder, ready: true, route: '/documents' },
  { key: 'settings', label: 'Settings', icon: Settings },
];

// CR6 — the Nine Co INTERNAL console. Coach sees assigned-scoped Overview /
// Clients / GCU Monitor / Question Bank (read). Analytics + Audit Log are
// cross-company governance surfaces → admin-only (hidden for a coach; the server
// also hard-gates them with 403). Overview is the GCU Monitor home, so a separate
// "GCU Monitor" item would duplicate it — the Overview item highlights for both.
const ADMIN_MENU: MenuItem[] = [
  { key: 'dashboard', label: 'Overview', icon: LayoutDashboard, ready: true, route: '/admin' },
  { key: 'clients', label: 'Clients', icon: Users, ready: true, route: '/admin/clients' },
  { key: 'bank', label: 'Question Bank', icon: ClipboardList, ready: true, route: '/admin/question-bank' },
  {
    key: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    ready: true,
    route: '/admin/analytics',
    adminOnly: true,
  },
  {
    key: 'audit',
    label: 'Audit Log',
    icon: ShieldCheck,
    ready: true,
    route: '/admin/audit',
    adminOnly: true,
  },
];

function initials(name: string) {
  return name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function AppShell({ active, children }: { active: string; children: ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isInternal = user?.role === 'admin' || user?.role === 'coach';
  const isAdmin = user?.role === 'admin';
  // Coaches see the internal surface but NOT the admin-only governance items
  // (Analytics / Audit) — the server hard-gates those too (403).
  const menu = isInternal
    ? ADMIN_MENU.filter((item) => !item.adminOnly || isAdmin)
    : OWNER_MENU;

  const asideClass = isInternal
    ? 'bg-slate-900 text-white'
    : 'bg-gradient-to-b from-blue-700 via-blue-600 to-sky-400 text-white';

  return (
    <div className="flex h-full gap-4 p-[18px]">
      <aside className={`flex w-60 shrink-0 flex-col rounded-3xl p-3 ${asideClass}`}>
        <div className="px-2 py-3">
          <Logo variant="onDark" subtitle={isInternal ? 'Admin Console' : 'by Nine Co'} />
        </div>
        {isInternal && (
          <div className="mx-2 mb-2 inline-flex w-max items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-bold tracking-wider">
            {user?.role === 'admin' ? 'ADMIN' : 'COACH'}
          </div>
        )}
        <nav className="mt-1 space-y-1">
          {menu.map((item) => {
            const Icon = item.icon;
            const on = item.key === active;
            const disabled = !item.ready;
            return (
              <button
                key={item.key}
                disabled={disabled}
                onClick={() => {
                  if (!disabled && item.route) navigate(item.route);
                }}
                title={disabled ? 'Tersedia pada CR berikutnya' : undefined}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                  on
                    ? isInternal
                      ? 'bg-white/10 text-white'
                      : 'bg-white text-blue-700 shadow-sm'
                    : 'text-white/85 hover:bg-white/10'
                } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                <Icon size={18} aria-hidden />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="mt-auto rounded-2xl bg-white/10 p-3 text-[11px] text-white/80">
          <div className="font-semibold text-white">General Check-Up</div>
          <div className="mt-0.5">
            {isInternal ? 'Nine Co · Konsol internal' : 'Foundation siap · CR1 selesai'}
          </div>
        </div>
      </aside>

      <main className="flex flex-1 flex-col overflow-hidden rounded-3xl bg-white shadow-soft">
        <div className="flex items-center gap-4 border-b border-slate-100 px-6 py-3.5">
          <div className="relative w-full max-w-sm">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Search size={18} aria-hidden />
            </span>
            <input
              placeholder="Cari…"
              aria-label="Cari"
              className="w-full rounded-full border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-4 text-sm outline-none focus:border-brand"
            />
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button
              className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-500"
              aria-label="Notifikasi"
            >
              <Bell size={18} aria-hidden />
            </button>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
                {user ? initials(user.name) : '–'}
              </span>
              <div className="leading-tight">
                <div className="text-sm font-semibold text-slate-800">{user?.name}</div>
                <div className="text-xs text-slate-400">{user?.email}</div>
              </div>
            </div>
            <button
              onClick={() => void logout()}
              className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-500 hover:text-slate-700"
              aria-label="Keluar"
              title="Keluar"
            >
              <LogOut size={18} aria-hidden />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-slate-50/40 p-6">{children}</div>
      </main>
    </div>
  );
}
